import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Radio, LayoutGrid, Swords, Video, Gift, ShieldCheck, X, ChevronLeft, ChevronRight, Sparkles } from "lucide-react";

const KEY = "aw_onboarded_v1";

const STEPS = [
  { icon: Sparkles, color: "#00FFC2", title: "Welcome to Air Wave 👋",
    text: "Streaming, unrestricted. Here's a 30-second tour of what makes Air Wave different from the rest." },
  { icon: Radio, color: "#FF0055", title: "Browse live channels",
    text: "Your home feed shows every live stream right now. Tap any card to jump straight into the action — ad-free." },
  { icon: LayoutGrid, color: "#00FFC2", title: "Multiview — watch 4 at once",
    text: "Follow multiple creators simultaneously in a single grid. No other platform makes this this easy.", to: "/multiview" },
  { icon: Swords, color: "#FFD600", title: "PK Battles",
    text: "Watch creators go head-to-head in live showdowns and vote for your favourite in real time.", to: "/pk" },
  { icon: Gift, color: "#FF0055", title: "Earn & interact",
    text: "Collect channel points, make predictions, play live trivia, and send virtual gifts to the streamers you love." },
  { icon: Video, color: "#00FFC2", title: "Go live yourself",
    text: "Open Creator Studio to start streaming or record a podcast — with a creator-first 95/5 revenue split.", to: "/dashboard" },
  { icon: ShieldCheck, color: "#FFD600", title: "Safe by design",
    text: "Robust parental controls and maturity gating keep the experience right for every viewer. You're all set — enjoy!" },
];

export const Walkthrough = () => {
  const { user } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [i, setI] = useState(0);

  const start = useCallback(() => { setI(0); setOpen(true); }, []);

  // Auto-show once for brand-new signed-in users.
  useEffect(() => {
    if (!user) return;
    let seen = true;
    try { seen = localStorage.getItem(KEY) === "1"; } catch (_) {}
    if (!seen) {
      const t = setTimeout(start, 900);
      return () => clearTimeout(t);
    }
  }, [user, start]);

  // Allow reopening from the menu.
  useEffect(() => {
    const h = () => start();
    window.addEventListener("open-walkthrough", h);
    return () => window.removeEventListener("open-walkthrough", h);
  }, [start]);

  const finish = () => {
    try { localStorage.setItem(KEY, "1"); } catch (_) {}
    setOpen(false);
  };

  if (!open) return null;
  const step = STEPS[i];
  const Icon = step.icon;
  const last = i === STEPS.length - 1;

  const next = () => {
    if (last) { finish(); return; }
    setI((v) => Math.min(v + 1, STEPS.length - 1));
  };

  return (
    <div className="fixed inset-0 z-[100] grid place-items-center p-4" data-testid="walkthrough">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={finish} />
      <div className="relative w-full max-w-md overflow-hidden rounded-3xl border border-white/10 bg-[#0C0C14] shadow-[0_30px_80px_-20px_rgba(0,0,0,0.9)]">
        <button onClick={finish} data-testid="walkthrough-skip" aria-label="Skip walkthrough"
          className="absolute right-3 top-3 z-10 grid h-8 w-8 place-items-center rounded-full bg-white/5 text-white/60 transition-colors hover:bg-white/10 hover:text-white">
          <X size={16} />
        </button>

        <div className="px-7 pb-6 pt-9 text-center">
          <div className="mx-auto grid h-16 w-16 place-items-center rounded-2xl transition-transform"
            style={{ background: `${step.color}22` }}>
            <Icon size={30} style={{ color: step.color }} />
          </div>
          <h2 className="mt-5 font-display text-2xl font-black tracking-tight">{step.title}</h2>
          <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-white/70">{step.text}</p>

          {step.to && (
            <button
              data-testid="walkthrough-jump"
              onClick={() => { finish(); nav(step.to); }}
              className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-white/10 px-4 py-1.5 text-xs font-semibold text-[#00FFC2] transition-colors hover:border-[#00FFC2]">
              Take me there <ChevronRight size={13} />
            </button>
          )}
        </div>

        {/* Progress dots */}
        <div className="flex items-center justify-center gap-1.5 pb-5">
          {STEPS.map((_, idx) => (
            <span key={idx}
              className={`h-1.5 rounded-full transition-all ${idx === i ? "w-6 bg-[#00FFC2]" : "w-1.5 bg-white/20"}`} />
          ))}
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-white/10 px-5 py-4">
          <button
            data-testid="walkthrough-back"
            onClick={() => setI((v) => Math.max(v - 1, 0))}
            disabled={i === 0}
            className="flex items-center gap-1 rounded-full px-3 py-2 text-sm text-white/60 transition-colors hover:text-white disabled:opacity-30">
            <ChevronLeft size={16} /> Back
          </button>
          <button onClick={finish} data-testid="walkthrough-skip-text"
            className="text-xs text-white/40 transition-colors hover:text-white/70">Skip tour</button>
          <button
            data-testid="walkthrough-next"
            onClick={next}
            className="flex items-center gap-1.5 rounded-full bg-[#00FFC2] px-5 py-2 text-sm font-bold text-black transition-transform hover:-translate-y-0.5">
            {last ? "Get started" : "Next"} {!last && <ChevronRight size={16} />}
          </button>
        </div>
      </div>
    </div>
  );
};
